/**
 * Fichier servi à la racine, à côté de `index.html`, et **non compilé dans le bundle** : c'est
 * lui qui permet à une même image de servir plusieurs environnements. L'image embarque celui de
 * sa configuration de build (`src/environments/config/<configuration>/config.json`) ; un
 * environnement le remplace en montant le sien à `/usr/share/nginx/html/config.json` — c'est
 * ce que font les previews.
 */
export const RUNTIME_CONFIG_URL = '/config.json';

/** Ce qu'un environnement fournit au démarrage plutôt qu'à la compilation. */
export interface RuntimeConfig {
  /** Adresse absolue de l'API, sans barre finale : `https://cdt.dev.primeservices.fr/api`. */
  apiUrl: string;
  keycloak: KeycloakRuntimeConfig;
}

export interface KeycloakRuntimeConfig {
  /** Adresse absolue de l'annuaire, sans barre finale : `https://auth.dev.primeservices.fr/auth`. */
  url: string;
  realm: string;
  clientId: string;
}

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

/**
 * Lit et valide {@link RUNTIME_CONFIG_URL}.
 *
 * **Rejette** plutôt que de retomber sur une valeur par défaut : une preview dont le fichier
 * n'a pas été monté parlerait sans bruit à l'API d'un autre environnement, et le défaut ne se
 * verrait qu'aux données affichées.
 */
export async function loadRuntimeConfig(
  fetchFn: FetchLike = (url, init) => fetch(url, init),
): Promise<RuntimeConfig> {
  return validate(await read(fetchFn));
}

async function read(fetchFn: FetchLike): Promise<unknown> {
  let response: Response;
  try {
    // `no-store` : sans lui, un redéploiement laisserait le navigateur sur la configuration
    // précédente, donc sur l'API d'un autre environnement.
    response = await fetchFn(RUNTIME_CONFIG_URL, { cache: 'no-store' });
  } catch (cause) {
    throw new Error(`${RUNTIME_CONFIG_URL} est injoignable : ${describe(cause)}`);
  }

  if (!response.ok) {
    throw new Error(`${RUNTIME_CONFIG_URL} n'a pas été servi (HTTP ${response.status}).`);
  }

  try {
    return await response.json();
  } catch (cause) {
    throw new Error(`${RUNTIME_CONFIG_URL} n'est pas un JSON exploitable : ${describe(cause)}`);
  }
}

function validate(parsed: unknown): RuntimeConfig {
  if (!isObject(parsed)) {
    throw new Error(`${RUNTIME_CONFIG_URL} ne contient pas un objet JSON.`);
  }

  const keycloak = isObject(parsed['keycloak']) ? parsed['keycloak'] : {};
  const invalid = [
    ...(isObject(parsed['keycloak']) ? [] : ['keycloak']),
    ...checkUrl('apiUrl', parsed['apiUrl']),
    ...checkUrl('keycloak.url', keycloak['url']),
    ...checkText('keycloak.realm', keycloak['realm']),
    ...checkText('keycloak.clientId', keycloak['clientId']),
  ];
  if (invalid.length > 0) {
    throw new Error(
      `${RUNTIME_CONFIG_URL} est incomplet ou invalide : ${invalid.join(', ')}. ` +
        `Attendu { "apiUrl": URL absolue, "keycloak": { "url": URL absolue, "realm", "clientId" } }.`,
    );
  }

  // Seuls les champs connus sont repris, et figés : la configuration ne bouge plus une fois
  // l'application démarrée.
  return Object.freeze({
    apiUrl: withoutTrailingSlash(parsed['apiUrl'] as string),
    keycloak: Object.freeze({
      url: withoutTrailingSlash(keycloak['url'] as string),
      realm: (keycloak['realm'] as string).trim(),
      clientId: (keycloak['clientId'] as string).trim(),
    }),
  });
}

function checkText(field: string, value: unknown): string[] {
  return typeof value === 'string' && value.trim() !== '' ? [] : [field];
}

function checkUrl(field: string, value: unknown): string[] {
  if (checkText(field, value).length > 0) {
    return [field];
  }
  try {
    const { protocol } = new URL(value as string);
    return protocol === 'https:' || protocol === 'http:' ? [] : [field];
  } catch {
    return [field];
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function withoutTrailingSlash(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function describe(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
